import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect } from 'react';
import authedFetch from '../lib/authedFetch';
import {
  ChevronRight, ChevronLeft, Check, Calculator, Calendar,
  Sparkles, AlertCircle, CheckCircle, Loader2, Info, X,
  Layers, CheckSquare, Square, ChevronDown, ChevronUp, Users, GraduationCap
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { StepSelectMeasures, StepEnterValues, StepReview } from './import/BulkImportSteps';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pillar colors
const PILLAR_COLORS = {
  P1: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', accent: 'text-blue-600' },
  P2: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-600' },
  P3: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', accent: 'text-purple-600' },
  P4: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', accent: 'text-amber-600' },
  P5: { bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-700', accent: 'text-rose-600' }
};

// Step indicator
const StepIndicator = ({ steps, currentStep }) => (
  <div className="flex items-center justify-center mb-6">
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        <div className="flex flex-col items-center">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
              index < currentStep
                ? 'bg-emerald-500 text-white'
                : index === currentStep
                ? 'bg-indigo-600 text-white ring-4 ring-indigo-100'
                : 'bg-gray-200 text-gray-500'
            }`}
          >
            {index < currentStep ? <Check size={18} /> : index + 1}
          </div>
          <span className={`text-xs mt-1 font-medium ${
            index === currentStep ? 'text-indigo-600' : 'text-gray-500'
          }`}>
            {step.label}
          </span>
        </div>
        {index < steps.length - 1 && (
          <div
            className={`w-12 h-1 mx-1 rounded ${
              index < currentStep ? 'bg-emerald-500' : 'bg-gray-200'
            }`}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Step 1: Set Period
const StepSetPeriod = ({ period, onPeriodChange }) => {
  const currentYear = new Date().getFullYear();

  const quarters = [
    { value: `${currentYear}-Q1`, label: `${currentYear} Q1 (Jan-Mar)` },
    { value: `${currentYear}-Q2`, label: `${currentYear} Q2 (Apr-Jun)` },
    { value: `${currentYear}-Q3`, label: `${currentYear} Q3 (Jul-Sep)` },
    { value: `${currentYear}-Q4`, label: `${currentYear} Q4 (Oct-Dec)` },
    { value: `${currentYear - 1}-Q4`, label: `${currentYear - 1} Q4` },
    { value: `${currentYear - 1}-Q3`, label: `${currentYear - 1} Q3` },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Set Measurement Period</h3>
        <p className="text-gray-500 mt-1">This period will apply to all measures you import</p>
      </div>

      <div className="max-w-md mx-auto space-y-4">
        <div>
          <Label className="text-sm text-gray-600 mb-2 block">Select Period</Label>
          <Select value={period} onValueChange={onPeriodChange}>
            <SelectTrigger className="w-full" data-testid="bulk-period-select">
              <SelectValue placeholder="Select a period" />
            </SelectTrigger>
            <SelectContent>
              {quarters.map(q => (
                <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="text-center text-sm text-gray-500">or</div>

        <div>
          <Label className="text-sm text-gray-600 mb-2 block">Custom Period</Label>
          <Input
            type="text"
            placeholder="e.g., 2025-Q1, 2025-H1, Jan 2025"
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="text-center"
          />
        </div>
      </div>

      <Alert className="bg-indigo-50 border-indigo-200 max-w-md mx-auto">
        <Layers className="h-4 w-4 text-indigo-600" />
        <AlertDescription className="text-indigo-800 text-sm">
          <strong>Bulk Import:</strong> You'll be able to enter data for multiple measures 
          in the next steps, all tagged with this period.
        </AlertDescription>
      </Alert>
    </div>
  );
};

// Step 4: Review - extracted to BulkImportSteps.jsx

// Step 5: Success
const StepSuccess = ({ results, onClose, onImportMore }) => {
  const successCount = results.filter(r => r.success).length;
  const errorCount = results.filter(r => !r.success).length;

  return (
    <div className="text-center space-y-6 py-6">
      <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="text-emerald-600" size={48} />
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-gray-900">Bulk Import Complete!</h3>
        <p className="text-gray-500 mt-2">
          Successfully imported {successCount} measure{successCount !== 1 ? 's' : ''}
          {errorCount > 0 && ` (${errorCount} failed)`}
        </p>
      </div>

      {/* Results Summary */}
      <div className="max-w-md mx-auto space-y-2 text-left max-h-[250px] overflow-y-auto">
        {results.map((result, index) => {
          const pillarStyle = PILLAR_COLORS[result.pillar] || PILLAR_COLORS.P1;
          return (
            <div
              key={`${result.measure_id || 'result'}-${index}`}
              className={`p-3 rounded-lg border flex items-center justify-between ${
                result.success ? `${pillarStyle.border} ${pillarStyle.bg}` : 'border-rose-200 bg-rose-50'
              }`}
            >
              <div className="flex items-center gap-2">
                {result.success ? (
                  <CheckCircle size={16} className="text-emerald-600" />
                ) : (
                  <AlertCircle size={16} className="text-rose-600" />
                )}
                <span className="font-medium text-gray-900">{result.measure_name}</span>
              </div>
              {result.success && (
                <span className="font-bold text-indigo-600">
                  {result.calculated_score?.toFixed(1)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-4 pt-4">
        <Button variant="outline" onClick={onClose} data-testid="bulk-close-btn">
          Close
        </Button>
        <Button onClick={onImportMore} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-import-more-btn">
          <Sparkles size={16} className="mr-2" />
          Import More Data
        </Button>
      </div>
    </div>
  );
};

// Main Bulk Import Wizard
export default function BulkQuickImportWizard({ isOpen, onClose, formulas = [], onImportComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [period, setPeriod] = useState('');
  const [selectedMeasures, setSelectedMeasures] = useState([]);
  const [measureValues, setMeasureValues] = useState({});
  const [calculations, setCalculations] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importResults, setImportResults] = useState([]);
  
  // Segment state
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedCohortIds, setSelectedCohortIds] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);

  const steps = [
    { id: 'period', label: 'Period' },
    { id: 'segments', label: 'Segments' },
    { id: 'measures', label: 'Measures' },
    { id: 'values', label: 'Values' },
    { id: 'review', label: 'Review' },
    { id: 'success', label: 'Done' },
  ];

  // Fetch segments
  useEffect(() => {
    if (isOpen) {
      fetchSegments();
    }
  }, [isOpen]);

  const fetchSegments = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const [cohortsRes, programsRes] = await Promise.all([
        authedFetch(`${API_URL}/api/segments/cohorts`, { headers: { 'Authorization': `Bearer ${token}` } }),
        authedFetch(`${API_URL}/api/segments/programs`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (cohortsRes.ok) {
        const data = await cohortsRes.json();
        setCohorts(data.cohorts || []);
      }
      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data.programs || []);
      }
    } catch (error) {
      logger.error('Failed to fetch segments:', error);
    }
  };

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setPeriod('');
      setSelectedMeasures([]);
      setMeasureValues({});
      setCalculations({});
      setImportResults([]);
      setSelectedCohortIds([]);
      setSelectedProgramIds([]);
    }
  }, [isOpen]);

  // Calculate previews when entering review
  useEffect(() => {
    if (currentStep === 4) {
      calculateAllPreviews();
    }
  }, [currentStep]);

  const calculateAllPreviews = async () => {
    setIsCalculating(true);
    const token = secureStorage.getItem('session_token');
    const newCalculations = {};

    for (const measureId of selectedMeasures) {
      const values = measureValues[measureId] || {};
      const rawData = {};
      Object.entries(values).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          rawData[key] = parseFloat(value);
        }
      });

      if (Object.keys(rawData).length > 0) {
        try {
          const response = await authedFetch(`${API_URL}/api/lei-components/calculate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ measure_id: measureId, raw_data: rawData })
          });

          if (response.ok) {
            const data = await response.json();
            newCalculations[measureId] = data;
          }
        } catch (error) {
          logger.error(`Calculation error for ${measureId}:`, error);
        }
      }
    }

    setCalculations(newCalculations);
    setIsCalculating(false);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const token = secureStorage.getItem('session_token');
    const results = [];

    for (const measureId of selectedMeasures) {
      const values = measureValues[measureId] || {};
      const rawData = {};
      Object.entries(values).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          rawData[key] = parseFloat(value);
        }
      });

      if (Object.keys(rawData).length > 0) {
        try {
          const response = await authedFetch(`${API_URL}/api/lei-components/import`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              measure_id: measureId,
              raw_data: rawData,
              period: period,
              cohort_ids: selectedCohortIds,
              program_ids: selectedProgramIds,
              source: 'bulk_quick_import'
            })
          });

          const data = await response.json();
          const formula = formulas.find(f => f.id === measureId);
          
          results.push({
            measure_id: measureId,
            measure_name: formula?.name || measureId,
            pillar: formula?.pillar,
            success: response.ok,
            calculated_score: data.calculation_result?.calculated_score,
            error: !response.ok ? data.detail : null
          });
        } catch (error) {
          const formula = formulas.find(f => f.id === measureId);
          results.push({
            measure_id: measureId,
            measure_name: formula?.name || measureId,
            pillar: formula?.pillar,
            success: false,
            error: error.message
          });
        }
      }
    }

    setImportResults(results);
    setCurrentStep(4);
    
    const successCount = results.filter(r => r.success).length;
    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} measure${successCount !== 1 ? 's' : ''}!`);
      if (onImportComplete) {
        onImportComplete(results);
      }
    }
    
    setIsSubmitting(false);
  };

  const handleToggleMeasure = (measureId) => {
    setSelectedMeasures(prev =>
      prev.includes(measureId)
        ? prev.filter(id => id !== measureId)
        : [...prev, measureId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMeasures.length === formulas.length) {
      setSelectedMeasures([]);
    } else {
      setSelectedMeasures(formulas.map(f => f.id));
    }
  };

  const handleSelectPillar = (pillar, selected) => {
    const pillarMeasureIds = formulas.filter(f => f.pillar === pillar).map(f => f.id);
    if (selected) {
      setSelectedMeasures(prev => [...new Set([...prev, ...pillarMeasureIds])]);
    } else {
      setSelectedMeasures(prev => prev.filter(id => !pillarMeasureIds.includes(id)));
    }
  };

  const handleValueChange = (measureId, componentId, value) => {
    setMeasureValues(prev => ({
      ...prev,
      [measureId]: {
        ...(prev[measureId] || {}),
        [componentId]: value
      }
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return period.trim() !== '';
      case 1:
        return true; // Segments are optional
      case 2:
        return selectedMeasures.length > 0;
      case 3:
        // At least one measure should have data
        return selectedMeasures.some(measureId => {
          const values = measureValues[measureId] || {};
          return Object.values(values).some(v => v !== '' && v !== null);
        });
      case 4:
        return Object.keys(calculations).length > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep === 4) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const toggleCohort = (cohortId) => {
    setSelectedCohortIds(prev =>
      prev.includes(cohortId)
        ? prev.filter(id => id !== cohortId)
        : [...prev, cohortId]
    );
  };

  const toggleProgram = (programId) => {
    setSelectedProgramIds(prev =>
      prev.includes(programId)
        ? prev.filter(id => id !== programId)
        : [...prev, programId]
    );
  };

  const handleImportMore = () => {
    setCurrentStep(0);
    setPeriod('');
    setSelectedMeasures([]);
    setMeasureValues({});
    setCalculations({});
    setImportResults([]);
    setSelectedCohortIds([]);
    setSelectedProgramIds([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-import-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Layers className="text-indigo-600" size={24} />
            Bulk Quick Import
          </DialogTitle>
          <DialogDescription>
            Import data for multiple LEI measures in a single session
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        {currentStep < 5 && <StepIndicator steps={steps} currentStep={currentStep} />}

        {/* Step Content */}
        <div className="min-h-[450px]">
          {currentStep === 0 && (
            <StepSetPeriod period={period} onPeriodChange={setPeriod} />
          )}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Assign to Segments (Optional)</h3>
                <p className="text-gray-500 mt-1">All imported data will be linked to selected cohorts and programs</p>
              </div>

              {/* Cohorts */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Users size={16} className="text-blue-600" />
                  Cohorts
                </Label>
                {cohorts.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No cohorts available</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                    {cohorts.map(cohort => (
                      <label
                        key={cohort.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          selectedCohortIds.includes(cohort.id)
                            ? 'bg-blue-50 border border-blue-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Checkbox
                          checked={selectedCohortIds.includes(cohort.id)}
                          onCheckedChange={() => toggleCohort(cohort.id)}
                        />
                        <span className="font-medium text-sm">{cohort.name}</span>
                        <Badge variant="outline" className="text-xs">{cohort.type}</Badge>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Programs */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <GraduationCap size={16} className="text-purple-600" />
                  Programs
                </Label>
                {programs.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No programs available</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                    {programs.map(program => (
                      <label
                        key={program.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          selectedProgramIds.includes(program.id)
                            ? 'bg-purple-50 border border-purple-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Checkbox
                          checked={selectedProgramIds.includes(program.id)}
                          onCheckedChange={() => toggleProgram(program.id)}
                        />
                        <span className="font-medium text-sm">{program.name}</span>
                        <Badge variant="outline" className="text-xs">{program.status}</Badge>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  Segmentation is optional. Skip this step if you don't need to filter data by group.
                </AlertDescription>
              </Alert>
            </div>
          )}
          {currentStep === 2 && (
            <StepSelectMeasures
              formulas={formulas}
              selectedMeasures={selectedMeasures}
              onToggleMeasure={handleToggleMeasure}
              onSelectAll={handleSelectAll}
              onSelectPillar={handleSelectPillar}
            />
          )}
          {currentStep === 3 && (
            <StepEnterValues
              formulas={formulas}
              selectedMeasures={selectedMeasures}
              measureValues={measureValues}
              onValueChange={handleValueChange}
            />
          )}
          {currentStep === 4 && (
            <StepReview
              formulas={formulas}
              selectedMeasures={selectedMeasures}
              measureValues={measureValues}
              period={period}
              calculations={calculations}
              isCalculating={isCalculating}
              cohorts={cohorts}
              programs={programs}
              selectedCohortIds={selectedCohortIds}
              selectedProgramIds={selectedProgramIds}
            />
          )}
          {currentStep === 5 && (
            <StepSuccess
              results={importResults}
              onClose={onClose}
              onImportMore={handleImportMore}
            />
          )}
        </div>

        {/* Navigation */}
        {currentStep < 5 && (
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0}
              data-testid="bulk-back-btn"
            >
              <ChevronLeft size={16} className="mr-1" />
              Back
            </Button>
            
            <span className="text-sm text-gray-500">
              Step {currentStep + 1} of {steps.length - 1}
            </span>

            <Button
              onClick={handleNext}
              disabled={!canProceed() || isSubmitting || isCalculating}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="bulk-next-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Importing...
                </>
              ) : currentStep === 4 ? (
                <>
                  <Check size={16} className="mr-1" />
                  Import All ({Object.keys(calculations).length})
                </>
              ) : (
                <>
                  Next
                  <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
